/**
 * GroupMemberNamesTooltip — what this catalog name is called by the people who
 * sell it.
 *
 * The catalog shows ONE name per item: `inventory_items.description`, which for
 * an imported item is an AI canonical rename ("Whole Milk"). That name is good
 * for the kitchen and useless in an aisle or on the phone to a rep, because it
 * may share no words with what any vendor actually prints ("WHL MLK GAL 4/1").
 * Worse, for a folded EQUIVALENT group the row shows the elected canonical
 * member's description, so the other members' wording is not on screen at all.
 *
 * This is the way back to it: hover (or tap) the name and see every pooled
 * purchase option in the VENDOR's terms — their vendor, their full product
 * wording, their SKU, their pack, their price. Nothing here is our language.
 *
 * It renders null when there is nothing to add: no options, or no option that
 * carries either vendor wording or a price. A tooltip that says nothing is
 * worse than no tooltip.
 *
 * Deliberately NOT built on HelpTooltip: that component is registry-driven
 * (utils/helpTopics) and returns null for any topic id it does not know, so it
 * cannot carry per-item content. The hover-intent + pinned-popover behaviour
 * here mirrors MultiVendorBadge so the two read as one system.
 *
 * Props:
 *   options        - pooled purchase_options rows for this item (group-pooled
 *                    by the host, so a folded row shows its siblings too)
 *   vendorNameById - { [vendor_id]: display name }, for options that carry an
 *                    id rather than free text
 *   itemName       - the catalog name being explained, shown as the heading
 *   mobile?        - true → tap to open a bottom sheet instead of hovering
 *   children       - the name element the tooltip is attached to
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Pointer must rest this long before hover alone opens the panel, so sweeping
// the cursor down a dense catalog does not strobe popovers.
const HOVER_INTENT_MS = 150;

const text = (value) => (value == null ? '' : String(value).trim());

const money = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? `$${n.toFixed(2)}` : null;
};

/**
 * One option as the vendor states it. Everything is the vendor's own field —
 * the only thing we contribute is the fallback labels when a field is empty.
 */
export function vendorTermsOf(option, vendorNameById = {}) {
  const o = option && typeof option === 'object' ? option : {};
  const vendor = text(o.vendor_name_text)
    || text(vendorNameById[o.vendor_id])
    || text(o.vendor)
    || 'Unknown vendor';
  const price = money(o.package_price);
  const unit = text(o.purchase_unit);
  return {
    id: o.id != null ? String(o.id) : null,
    vendor,
    // The vendor's own product wording. Null (not our description) when they
    // never sent one — "we do not know what they call it" is the honest answer.
    productName: text(o.vendor_description) || null,
    sku: text(o.vendor_sku) || null,
    pack: text(o.pack_description) || null,
    price,
    priceLabel: price ? (unit ? `${price} / ${unit}` : price) : null,
    inactive: text(o.status) !== '' && text(o.status) !== 'active',
  };
}

/** The rows worth showing, vendor A→Z then cheapest first within a vendor. */
export function buildVendorTermRows(options, vendorNameById = {}) {
  const rows = (Array.isArray(options) ? options : [])
    .map((option) => vendorTermsOf(option, vendorNameById))
    .filter((row) => row.productName || row.price || row.sku);
  rows.sort((a, b) => {
    const byVendor = a.vendor.localeCompare(b.vendor);
    if (byVendor !== 0) return byVendor;
    const ap = a.price ? Number(a.price.slice(1)) : Number.POSITIVE_INFINITY;
    const bp = b.price ? Number(b.price.slice(1)) : Number.POSITIVE_INFINITY;
    return ap - bp;
  });
  return rows;
}

const styles = {
  wrap: { position: 'relative', display: 'block' },
  panel: {
    position: 'absolute',
    zIndex: 1200,
    top: '100%',
    left: 0,
    marginTop: 4,
    minWidth: 280,
    maxWidth: 420,
    background: '#fffdfa',
    border: '1px solid #d8cdc3',
    borderRadius: 8,
    boxShadow: '0 6px 20px rgba(61, 37, 24, 0.16)',
    padding: '10px 12px',
    color: '#3d2518',
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'left',
    cursor: 'default',
  },
  heading: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#5A4A42',
    marginBottom: 6,
  },
  ourName: { color: '#8a7a72', fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  row: { paddingTop: 6, marginTop: 6, borderTop: '1px solid #f0e6dd' },
  firstRow: { paddingTop: 0, marginTop: 0, borderTop: 'none' },
  vendor: { fontWeight: 700, color: '#5A4A42' },
  product: { color: '#3d2518' },
  meta: { color: '#8a7a72', fontSize: 11 },
  price: { float: 'right', fontWeight: 700, color: '#3d2518', fontVariantNumeric: 'tabular-nums' },
  noName: { color: '#8a7a72', fontStyle: 'italic' },
  sheetOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(61,37,24,0.38)', zIndex: 2000,
    display: 'flex', alignItems: 'flex-end',
  },
  sheet: {
    background: '#fffdfa', width: '100%', borderTopLeftRadius: 14, borderTopRightRadius: 14,
    padding: '14px 16px 22px', maxHeight: '70vh', overflowY: 'auto', color: '#3d2518', fontSize: 13,
  },
};

function VendorTermsList({ rows, itemName }) {
  return (
    <>
      <div style={styles.heading}>
        In vendor terms
        {itemName ? <span style={styles.ourName}> — we call it “{itemName}”</span> : null}
      </div>
      {rows.map((row, index) => (
        <div key={row.id || `${row.vendor}-${index}`} style={index === 0 ? { ...styles.row, ...styles.firstRow } : styles.row}>
          {row.priceLabel ? <span style={styles.price}>{row.priceLabel}</span> : null}
          <div style={styles.vendor}>{row.vendor}</div>
          <div style={styles.product}>
            {row.productName || <span style={styles.noName}>no product name on file</span>}
          </div>
          {(row.sku || row.pack || row.inactive) && (
            <div style={styles.meta}>
              {[row.sku ? `#${row.sku}` : null, row.pack, row.inactive ? 'inactive' : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export default function GroupMemberNamesTooltip({
  options,
  vendorNameById,
  itemName,
  mobile = false,
  // Set on hosts whose name element is itself interactive — the catalog's Name
  // cell is an editable <input>, and clicking it must start editing, never pin
  // a popover. Hover opens and leaving closes; there is no pinned state.
  hoverOnly = false,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const hoverTimer = useRef(null);
  const wrapRef = useRef(null);

  const rows = buildVendorTermRows(options, vendorNameById);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  // A pinned panel closes on an outside click, like MultiVendorBadge's.
  useEffect(() => {
    if (!pinned) return undefined;
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pinned]);

  if (!rows.length) return children;

  if (mobile) {
    return (
      <span ref={wrapRef} style={styles.wrap}>
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => { event.stopPropagation(); setOpen(true); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              setOpen(true);
            }
          }}
        >
          {children}
        </span>
        {open && createPortal(
          <div
            style={styles.sheetOverlay}
            onClick={(event) => { event.stopPropagation(); setOpen(false); }}
          >
            <div style={styles.sheet} onClick={(event) => event.stopPropagation()}>
              <VendorTermsList rows={rows} itemName={itemName} />
            </div>
          </div>,
          document.body,
        )}
      </span>
    );
  }

  return (
    <span
      ref={wrapRef}
      style={styles.wrap}
      onMouseEnter={() => {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setOpen(true), HOVER_INTENT_MS);
      }}
      onMouseLeave={() => {
        clearTimeout(hoverTimer.current);
        if (!pinned) setOpen(false);
      }}
    >
      {hoverOnly ? children : (
        <span onClick={() => { setPinned((was) => !was); setOpen(true); }}>
          {children}
        </span>
      )}
      {open && (
        <div style={styles.panel} onClick={(event) => event.stopPropagation()}>
          <VendorTermsList rows={rows} itemName={itemName} />
        </div>
      )}
    </span>
  );
}
